'use client';

import { useId } from 'react';
import { create } from 'zustand';
import Link from '@eliadmin/core/src/components/link';
import { CmsIcon } from '@eliadmin/core/src/components/nt/ui/cms-image';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { MenuBrand } from '../header-types';

const INITIAL_BRANDS = 8;

// All header category panels share this choice, including when a panel is remounted.
const useBrandExpansion = create<{ expanded: boolean; toggle: () => void }>(set => ({
  expanded: false,
  toggle: () => set(state => ({ expanded: !state.expanded })),
}));

/** Compact logo navigation; expand in place while keeping the selected toner type in every URL. */
export function BrandTiles({
  brands,
  className,
  itemClassName,
  tileClassName,
}: {
  brands: MenuBrand[];
  className?: string;
  itemClassName?: string;
  tileClassName?: string;
}) {
  const { expanded, toggle } = useBrandExpansion();
  const listId = useId();
  const visible = expanded ? brands : brands.slice(0, INITIAL_BRANDS);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ul
        id={listId}
        role="list"
        aria-label="Značky tlačiarní"
        className={cn('grid gap-2', className)}
      >
        {visible.map(brand => (
          <li key={brand.href} className={cn('flex', !expanded && itemClassName)}>
            <Link
              href={brand.href}
              aria-label={brand.label}
              prefetch={false}
              className={cn(
                'flex h-16 w-full items-center justify-center overflow-hidden rounded-xl border border-[#E2E5DF] px-2 py-0 transition-colors hover:border-[#127A26] hover:bg-[#F5F6F3] focus-visible:shadow-focus focus-visible:outline-none',
                tileClassName,
              )}
            >
              {brand.image ? (
                <CmsIcon
                  image={brand.image}
                  sizes="(min-width: 1280px) 160px, 180px"
                  className="h-20 w-full"
                />
              ) : (
                <span className="nt-body-l-bold text-center text-[#0D110D]">{brand.label}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
      {brands.length > INITIAL_BRANDS ? (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={toggle}
          className="nt-button-m min-h-9 self-center rounded text-[#127A26] underline underline-offset-4 hover:text-[#0E6A20] focus-visible:shadow-focus focus-visible:outline-none"
        >
          {expanded ? 'Zobraziť menej značiek' : 'Zobraziť všetky značky'}
        </button>
      ) : null}
    </div>
  );
}
